/**
 * Comprehensive Error Handling System
 * Defines error types, categories, and structures for consistent error handling
 */

// Error Categories for classification
export const ErrorCategory = {
  NETWORK: 'network',
  API: 'api',
  ROUTING: 'routing',
  AUTHENTICATION: 'authentication',
  VALIDATION: 'validation',
  STORAGE: 'storage',
  GEOCODING: 'geocoding',
  UI: 'ui',
  UNKNOWN: 'unknown'
} as const;

export type ErrorCategory = typeof ErrorCategory[keyof typeof ErrorCategory];

// Error Severity Levels
export const ErrorSeverity = {
  LOW: 'low',          // Minor issues, user can continue
  MEDIUM: 'medium',    // Significant issues, some functionality affected
  HIGH: 'high',        // Critical issues, major functionality broken
  CRITICAL: 'critical' // System-breaking issues
} as const;

export type ErrorSeverity = typeof ErrorSeverity[keyof typeof ErrorSeverity];

// Structured Error Interface
export interface CyclingBuddyError {
  // Core error properties
  id: string;                    // Unique error identifier
  category: ErrorCategory;       // Error classification
  severity: ErrorSeverity;       // Impact level
  code?: string;                // Specific error code (e.g., 'ROUTE_404', 'AUTH_EXPIRED')
  
  // Messages
  message: string;              // User-friendly message
  technicalMessage?: string;    // Technical details for developers
  
  // Context
  timestamp: Date;              // When the error occurred
  userId?: string;              // User ID if available
  sessionId?: string;           // Session ID for tracking
  url?: string;                 // Current page/route where error occurred
  userAgent?: string;           // Browser info
  
  // Additional data
  context?: Record<string, any>; // Additional context data
  originalError?: Error;         // Original error object if caught
  stackTrace?: string;          // Stack trace for debugging
  
  // Action suggestions
  actions?: ErrorAction[];       // Suggested actions for user
  retryable?: boolean;          // Whether the operation can be retried
  retryCount?: number;          // Number of retry attempts
}

// Action suggestions for users
export interface ErrorAction {
  label: string;                // Button/link text
  type: 'retry' | 'redirect' | 'refresh' | 'contact' | 'custom';
  action?: () => void | Promise<void>; // Custom action function
  url?: string;                 // URL for redirect actions
}

// Error Context for additional information
export interface ErrorContext {
  feature?: string;             // Which feature was being used
  operation?: string;           // What operation was attempted
  parameters?: Record<string, any>; // Operation parameters
  apiEndpoint?: string;         // API endpoint that failed
  httpStatus?: number;          // HTTP status code
  responseData?: any;           // API response data
}

// Predefined error codes for common scenarios
export const ErrorCodes = {
  // Network errors
  NETWORK_OFFLINE: 'NETWORK_OFFLINE',
  NETWORK_TIMEOUT: 'NETWORK_TIMEOUT',
  NETWORK_UNAVAILABLE: 'NETWORK_UNAVAILABLE',
  
  // API errors
  API_UNAUTHORIZED: 'API_UNAUTHORIZED',
  API_FORBIDDEN: 'API_FORBIDDEN',
  API_NOT_FOUND: 'API_NOT_FOUND',
  API_RATE_LIMIT: 'API_RATE_LIMIT',
  API_SERVER_ERROR: 'API_SERVER_ERROR',
  API_INVALID_RESPONSE: 'API_INVALID_RESPONSE',
  
  // Routing errors
  ROUTE_NO_PATH: 'ROUTE_NO_PATH',
  ROUTE_INVALID_WAYPOINTS: 'ROUTE_INVALID_WAYPOINTS',
  ROUTE_START_NOT_MAPPED: 'ROUTE_START_NOT_MAPPED',
  ROUTE_END_NOT_MAPPED: 'ROUTE_END_NOT_MAPPED',
  ROUTE_WAYPOINT_NOT_MAPPED: 'ROUTE_WAYPOINT_NOT_MAPPED',
  ROUTE_MAP_DATA_MISSING: 'ROUTE_MAP_DATA_MISSING',
  ROUTE_CALCULATION_FAILED: 'ROUTE_CALCULATION_FAILED',
  
  // Authentication errors
  AUTH_EXPIRED: 'AUTH_EXPIRED',
  AUTH_INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  AUTH_EMAIL_NOT_CONFIRMED: 'AUTH_EMAIL_NOT_CONFIRMED',
  AUTH_USER_NOT_FOUND: 'AUTH_USER_NOT_FOUND',
  
  // Validation errors
  VALIDATION_REQUIRED_FIELD: 'VALIDATION_REQUIRED_FIELD',
  VALIDATION_INVALID_EMAIL: 'VALIDATION_INVALID_EMAIL',
  VALIDATION_PASSWORD_WEAK: 'VALIDATION_PASSWORD_WEAK',
  VALIDATION_NAME_EXISTS: 'VALIDATION_NAME_EXISTS',
  
  // Storage errors
  STORAGE_SAVE_FAILED: 'STORAGE_SAVE_FAILED',
  STORAGE_LOAD_FAILED: 'STORAGE_LOAD_FAILED',
  STORAGE_DELETE_FAILED: 'STORAGE_DELETE_FAILED',
  STORAGE_QUOTA_EXCEEDED: 'STORAGE_QUOTA_EXCEEDED',
  
  // Geocoding errors
  GEOCODING_FAILED: 'GEOCODING_FAILED',
  GEOCODING_NO_RESULTS: 'GEOCODING_NO_RESULTS',
  GEOCODING_RATE_LIMIT: 'GEOCODING_RATE_LIMIT',
  
  // UI errors
  UI_COMPONENT_CRASH: 'UI_COMPONENT_CRASH',
  UI_INVALID_STATE: 'UI_INVALID_STATE',
} as const;

export type ErrorCode = typeof ErrorCodes[keyof typeof ErrorCodes];
